import type { SelectChangeEvent } from '@mui/material'
import type { GridFilterItem } from '@mui/x-data-grid'
import { gridFilterModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import GridSearchOutlined from '@/components/dataDisplay/GridSearchOutlined'
import GridToolbar from '@/components/dataDisplay/GridToolbar'
import CountryFilter from '@/components/datagrid/CountryFilter'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import { FIELD_WIDTH } from '@/constants/layout'
import type { Portfolio } from '@/features/bidding/types/bid'
import { PortfolioState } from '@/features/bidding/types/bid'
import { getFilterItemValue, updateDataGridFilter } from '@/utils/datagrid/filters'
import { filterUnique } from '@/utils/filterUnique'
import { sortOptionsAlphabetically } from '@/utils/sortOptionsAlphabetically'

/**
 * Module augmentation is necessary to use slotProps without TypeScript errors.
 */
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends PortfoliosDataGridToolbarProps {}
}

export type PortfoliosDataGridToolbarProps = {
  portfolios: Portfolio[] | null
}

function PortfoliosDataGridToolbar({ portfolios }: Readonly<PortfoliosDataGridToolbarProps>) {
  const { t } = useTranslation()
  const apiRef = useGridApiContext()

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)
  const countryCode = getFilterItemValue<string>(filterModel, 'countryCode') ?? ''
  const priceArea = getFilterItemValue<string>(filterModel, 'priceArea') ?? ''
  const state = getFilterItemValue<string>(filterModel, 'state') ?? ''

  const handleFilterChange = (field: string, value: string) => {
    updateDataGridFilter(apiRef, field, { id: field, field, operator: 'equals', value })
  }

  const blankOption = { value: '', label: '-', id: `filter-item-blank` }

  const countryCodeOptions = useMemo(() => {
    const uniqueCountryCodes = new Set<CountryIdentifier>()
    for (const portfolio of portfolios || []) {
      uniqueCountryCodes.add(portfolio.countryCode.toLowerCase() as CountryIdentifier)
    }
    return Array.from(uniqueCountryCodes)
  }, [portfolios])

  const priceAreaOptions = useMemo(() => {
    const selectFields = (portfolios || []).map((portfolio) => ({
      id: portfolio.priceArea,
      value: portfolio.priceArea,
      label: portfolio.priceArea,
    }))
    return sortOptionsAlphabetically(filterUnique(selectFields))
  }, [portfolios])

  const stateOptions = useMemo(
    () =>
      Object.values(PortfolioState).map((value) => ({
        id: value,
        label: t(`portfolio_hierarchy.portfolioStates.${value}` as any),
        value: value,
      })),
    [t],
  )

  return (
    <GridToolbar
      filters={
        <>
          <GridSearchOutlined label={t('common.navigation.search')} placeholder={t('portfolio_hierarchy.search')} />

          <CountryFilter
            countryOptions={countryCodeOptions}
            field="countryCode"
            value={countryCode}
            onChange={(items: GridFilterItem[]) => handleFilterChange('countryCode', items[0]?.value as string)}
          />
          <CustomSelectField
            data-testid="price-area-selector"
            id="priceArea"
            label={t('portfolio_hierarchy.portfolio.price_area')}
            options={[blankOption, ...priceAreaOptions]}
            sx={{ minWidth: FIELD_WIDTH }}
            value={priceArea}
            onChange={(event: SelectChangeEvent) => handleFilterChange('priceArea', event.target.value)}
          />
          <CustomSelectField
            data-testid="state-selector"
            id="state"
            label={t('portfolio_hierarchy.portfolio.state')}
            options={[blankOption, ...stateOptions]}
            sx={{ minWidth: FIELD_WIDTH }}
            value={state}
            onChange={(event: SelectChangeEvent) => handleFilterChange('state', event.target.value)}
          />
        </>
      }
    />
  )
}

export default PortfoliosDataGridToolbar
